/**
 * A day of sun (Sprint 084.0, ADR-0091; DEC-6).
 *
 * Sunrise and sunset are **astronomical**: the sun's centre at −0.833° on a
 * flat horizon. Sunrise behind a hill is a different question, and V2's.
 *
 * Polar day and polar night are **values**, never exceptions and never a clamp
 * to the horizon. Tromsø in June has a solar noon and no sunrise, and says so.
 *
 * The year's solstices and equinoxes are **computed** — the instants the sun's
 * apparent longitude reaches 0°, 90°, 180° and 270° — and read as dates in the
 * site's zone. Fixed calendar days would be wrong: the December solstice of
 * 2027 is at 02:37 UTC on the 22nd, which is the 22nd in Paris. They are named
 * by month, never by season, because June's solstice is winter in Sydney.
 */
import { HORIZON_DEGREES, apparentLongitude, solarGeometry, sunPosition } from './position.js';
import { instantToLocal, localToInstant, nextDate } from './time.js';
const MINUTE = 60_000;
const DAY = 86_400_000;
const RADIANS = Math.PI / 180;
const DEGREES = 180 / Math.PI;
const PATH_STEP_MINUTES = 10;
export function solarDay(localDate, zone, latitude, longitude, options = {}) {
    // To the minute, as the events are: a noon at 11:38:40 UTC is 13:39 in
    // Paris, as USNO publishes it, not 13:38.
    const noon = Math.round(solarNoonOf(localDate, zone, longitude) / MINUTE) * MINUTE;
    const rise = eventNear(noon, zone, latitude, longitude, 'rise');
    const set = eventNear(noon, zone, latitude, longitude, 'set');
    const dayStart = localToInstant(localDate, '00:00', zone);
    const dayEnd = localToInstant(nextDate(localDate), '00:00', zone);
    const dayLength = rise.kind === 'at' && set.kind === 'at'
        ? Math.round((set.instant - rise.instant) / MINUTE)
        : rise.kind === 'none' && rise.reason === 'polar-day'
            ? Math.round((dayEnd - dayStart) / MINUTE)
            : 0;
    return {
        localDate,
        sunrise: rise,
        solarNoon: {
            instant: noon,
            localTime: instantToLocal(noon, zone).localTime,
            altitudeDegrees: sunPosition(noon, latitude, longitude).altitudeDegrees
        },
        sunset: set,
        dayLengthMinutes: dayLength,
        path: options.path === false ? [] : pathOf(dayStart, dayEnd, rise, set, latitude, longitude)
    };
}
const SEASONS = [
    { season: 'march-equinox', longitude: 0, month: 3, day: 20 },
    { season: 'june-solstice', longitude: 90, month: 6, day: 21 },
    { season: 'september-equinox', longitude: 180, month: 9, day: 23 },
    { season: 'december-solstice', longitude: 270, month: 12, day: 21 }
];
/** The year's two solstices and two equinoxes, to the minute, as local dates. */
export function seasonDates(year, zone) {
    const degreesPerDay = 360 / 365.2422;
    return SEASONS.map(({ season, longitude, month, day }) => {
        let instant = Date.UTC(year, month - 1, day, 12);
        // Newton on the apparent longitude, which moves about a degree a day.
        for (let step = 0; step < 6; step += 1) {
            const gap = ((longitude - apparentLongitude(instant) + 540) % 360) - 180;
            instant += (gap / degreesPerDay) * DAY;
        }
        const rounded = Math.round(instant / MINUTE) * MINUTE;
        return { season, instant: rounded, localDate: instantToLocal(rounded, zone).localDate };
    });
}
/** The events of the year's four reference days, without paths. */
export function referenceDays(year, zone, latitude, longitude) {
    return seasonDates(year, zone).map(({ season, localDate }) => ({
        season,
        day: solarDay(localDate, zone, latitude, longitude, { path: false })
    }));
}
/**
 * The instant of solar noon that falls on `localDate` in the zone. Local noon
 * of a date is, to first order, 12:00 UTC less the longitude's hours; a zone far
 * from its meridian (UTC+14 at 157° W) can put that on the neighbouring civil
 * date, which the one-day correction catches.
 */
function solarNoonOf(localDate, zone, longitude) {
    const [year, month, day] = localDate.split('-').map(Number);
    const midnightUtc = Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1);
    let noon = transitAfter(midnightUtc, longitude);
    const landed = instantToLocal(noon, zone).localDate;
    if (landed > localDate) {
        noon = transitAfter(midnightUtc - DAY, longitude);
    }
    else if (landed < localDate) {
        noon = transitAfter(midnightUtc + DAY, longitude);
    }
    return noon;
}
/** Solar transit on the UTC day starting at `midnightUtc`, refined for the equation of time. */
function transitAfter(midnightUtc, longitude) {
    let noon = midnightUtc + (720 - 4 * longitude) * MINUTE;
    for (let step = 0; step < 3; step += 1) {
        noon = midnightUtc + (720 - 4 * longitude - solarGeometry(noon).equationOfTimeMinutes) * MINUTE;
    }
    return noon;
}
function eventNear(noon, zone, latitude, longitude, which) {
    const sign = which === 'rise' ? -1 : 1;
    let instant = noon;
    for (let step = 0; step < 4; step += 1) {
        const { declinationDegrees, equationOfTimeMinutes } = solarGeometry(instant);
        const lat = latitude * RADIANS;
        const dec = declinationDegrees * RADIANS;
        const cosHourAngle = (Math.sin(HORIZON_DEGREES * RADIANS) - Math.sin(lat) * Math.sin(dec)) /
            (Math.cos(lat) * Math.cos(dec));
        if (cosHourAngle > 1) {
            return { kind: 'none', reason: 'polar-night' };
        }
        if (cosHourAngle < -1) {
            return { kind: 'none', reason: 'polar-day' };
        }
        const hourAngle = Math.acos(cosHourAngle) * DEGREES;
        // The transit this event belongs to, with the equation of time read at the
        // event rather than at noon: the refinement that keeps it within a minute.
        const transit = noon + (solarGeometry(noon).equationOfTimeMinutes - equationOfTimeMinutes) * MINUTE;
        instant = transit + sign * hourAngle * 4 * MINUTE;
    }
    const rounded = Math.round(instant / MINUTE) * MINUTE;
    return { kind: 'at', instant: rounded, localTime: instantToLocal(rounded, zone).localTime };
}
function pathOf(dayStart, dayEnd, rise, set, latitude, longitude) {
    if (rise.kind === 'none' && rise.reason === 'polar-night') {
        return [];
    }
    const from = rise.kind === 'at' ? rise.instant : dayStart;
    const to = set.kind === 'at' ? set.instant : dayEnd;
    const instants = [from];
    const step = PATH_STEP_MINUTES * MINUTE;
    for (let instant = Math.ceil(from / step) * step; instant < to; instant += step) {
        if (instant > from) {
            instants.push(instant);
        }
    }
    instants.push(to);
    return instants.map((instant) => {
        const { azimuthDegrees, altitudeDegrees } = sunPosition(instant, latitude, longitude);
        return { instant, azimuthDegrees, altitudeDegrees };
    });
}
//# sourceMappingURL=events.js.map